
  import Vue from 'vue'
  import Component from 'vue-class-component'
  import { Prop, Watch } from 'vue-property-decorator'
  import { namespace } from 'vuex-class'
  import { ILinkTrackingParams, IServiceProvider } from '@/model/Service/serviceProviderInterfaces'
  import { IErrors } from '@/model/IErrors'
  import { IAuthUserData } from '@/store/modules/authModule'
  import { PermissionHelper } from '@/helpers/PermissionHelper'
  import { EUserPermissions } from '@/model/EUserPermissions'

  const authModule = namespace('authModule')

  type Preset = 'none' | 'impact' | 'integrations_platform' | 'custom'

  // Parameter names each known destination expects
  const PRESETS: { [preset: string]: ILinkTrackingParams } = {
    impact: { user: 'subId1', community: 'subId2' },
    integrations_platform: { user: 'u', community: 'c' },
  }

  const EXAMPLE_VALUES = { user: '123', community: '45' }
  const EXAMPLE_LINK = 'https://partner.example.com/'

  @Component({})
  export default class SPLinkTracking extends Vue {

    @Prop() protected provider!: IServiceProvider
    @Prop() protected errors!: IErrors
    @Prop() protected editing!: boolean
    @Prop() protected viewing!: boolean
    @Prop() protected disabled!: boolean

    @authModule.Getter('userData')
    protected authUser!: IAuthUserData

    // "Custom" stays selected even while its values happen to match a preset
    private customSelected = false

    @Watch('provider.id')
    protected onProviderChange() {
      this.customSelected = false
    }

    protected get isDeveloper(): boolean {
      return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_DEVELOPER, 0)
    }

    protected get canEdit(): boolean {
      return !!this.editing && this.isDeveloper
    }

    protected get primaryColor(): string {
      const v = this.$vuetify
      return (v && v.theme && v.theme.currentTheme && (v.theme.currentTheme.primary as string)) || '#1976D2'
    }

    protected get headerIconWrapStyle(): object {
      return { background: this.primaryColor }
    }

    protected get params(): ILinkTrackingParams {
      return this.provider.link_tracking_params || {}
    }

    protected get hasParams(): boolean {
      return !!(this.params.user || this.params.community)
    }

    protected get preset(): Preset {
      if (this.customSelected) {
        return 'custom'
      }
      if (!this.hasParams) {
        return 'none'
      }
      const match = Object.keys(PRESETS).find(key =>
        PRESETS[key].user === this.params.user && PRESETS[key].community === this.params.community
      )
      return (match as Preset) || 'custom'
    }

    protected set preset(preset: Preset) {
      this.customSelected = preset === 'custom'
      if (preset === 'none') {
        this.provider.link_tracking_params = null
      } else if (PRESETS[preset]) {
        this.provider.link_tracking_params = { ...PRESETS[preset] }
      }
    }

    protected get presetItems() {
      return (['none', 'impact', 'integrations_platform', 'custom'] as Preset[])
        .map(value => ({ text: this.presetText(value), value }))
    }

    protected get userParam(): string {
      return this.params.user || ''
    }

    protected set userParam(value: string) {
      this.setParam('user', value)
    }

    protected get communityParam(): string {
      return this.params.community || ''
    }

    protected set communityParam(value: string) {
      this.setParam('community', value)
    }

    // The provider link as the app (user + community) and a standalone (community only) open it
    protected get previewLines() {
      const link = this.providerLink() || EXAMPLE_LINK
      return [
        {
          icon: 'mdi-cellphone',
          label: this.$t('services.provider.fields.link_tracking.preview_app') as string,
          url: this.withParams(link, EXAMPLE_VALUES),
        },
        {
          icon: 'mdi-monitor',
          label: this.$t('services.provider.fields.link_tracking.preview_standalone') as string,
          url: this.withParams(link, { community: EXAMPLE_VALUES.community }),
        },
      ]
    }

    protected presetText(preset: Preset): string {
      return this.$t('services.provider.fields.link_tracking.presets.' + preset) as string
    }

    private setParam(id: keyof ILinkTrackingParams, value: string) {
      const params: ILinkTrackingParams = { ...this.params }
      const paramName = (value || '').trim()
      if (paramName) {
        params[id] = paramName
      } else {
        delete params[id]
      }
      this.provider.link_tracking_params = Object.keys(params).length ? params : null
    }

    private providerLink(): string {
      const link: any = this.provider.link
      if (link && typeof link === 'object') {
        return Object.keys(link).map(locale => link[locale]).find(each => !!each) || ''
      }
      return link || ''
    }

    private withParams(link: string, values: { [id: string]: string }): string {
      let url: URL
      try {
        url = new URL(link)
      } catch (e) {
        url = new URL(EXAMPLE_LINK)
      }
      (Object.keys(this.params) as Array<keyof ILinkTrackingParams>).forEach(id => {
        const paramName = this.params[id]
        if (paramName && values[id]) {
          url.searchParams.set(paramName, values[id])
        }
      })
      return url.toString()
    }
  }
